import { ApolloClient, from, InMemoryCache, HttpLink, useLazyQuery as useLazyQuery$1, useMutation as useMutation$1, useQuery as useQuery$1 } from '@apollo/client';
export { ApolloConsumer, ApolloError, ApolloProvider, InMemoryCache, defaultDataIdFromObject, gql, makeVar, useApolloClient, useReactiveVar, useSubscription } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
export * from 'graphql';
export { MockedProvider } from '@apollo/client/testing';
import { useEffect } from 'react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0
THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.
See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}
function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var apolloClient;
var initApolloClient = function (uri, options) {
    // Make sure to create a new client for every server-side request so that data
    // isn't shared between connections
    if (typeof window === 'undefined') {
        return createApolloClient(uri, options);
    }
    if (!apolloClient) {
        apolloClient = createApolloClient(uri, options);
    }
    return apolloClient;
};
var createApolloClient = function (uri, options) {
    if (options === void 0) { options = {}; }
    if (!uri) {
        console.error('No graphQL endpoint specified.');
        throw new Error('No graphQL endpoint specified.');
    }
    var cacheConfig = options.cacheConfig || {};
    var initialState = options.initialState || {};
    var headers = {};
    if (options.apigeeKey) {
        headers['x-api-key'] = options.apigeeKey;
    }
    var errorLink = onError(function (_a) {
        var graphQLErrors = _a.graphQLErrors, networkError = _a.networkError;
        if (graphQLErrors) {
            graphQLErrors.forEach(function (error) { return console.error("[GraphQL error]: " + error.message); });
        }
        if (networkError) {
            console.error("[Network error]: " + networkError.message);
        }
    });
    var httpLink = new HttpLink({
        credentials: 'same-origin',
        fetch: fetch,
        headers: headers,
        uri: uri,
    });
    return new ApolloClient({
        ssrMode: typeof window !== 'undefined',
        link: from([errorLink, httpLink]),
        cache: new InMemoryCache(__assign({ typePolicies: {
                LegacyProduct: {
                    keyFields: ['sku'],
                },
            } }, cacheConfig)).restore(initialState),
    });
};

var wait = function (ms) { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
    return [2 /*return*/, new Promise(function (resolve) { return setTimeout(resolve, ms); })];
}); }); };

var getOperationInfo = function (query) {
    var operationNode = query.definitions[0];
    var name = 'anonymous';
    // Fallback name when the operation has been malformed but still send to the BE
    var type = operationNode.kind;
    if (operationNode.kind === 'OperationDefinition') {
        type = operationNode.operation;
        if (operationNode.name) {
            name = operationNode.name.value;
        }
    }
    return [type, name];
};
var handleError = function (query, error) {
    var graphQLErrors = error.graphQLErrors;
    var Sentry = window.Sentry;
    var _a = __read(getOperationInfo(query), 2), type = _a[0], name = _a[1];
    // logging package does not have scope or capture exceptions as of yet.
    if (Sentry) {
        if (graphQLErrors.length) {
            Sentry.withScope(function (scope) {
                scope.setTag('type', 'graphql');
                Sentry.captureException(error);
            });
        }
        else {
            Sentry.captureException(error);
        }
    }
    console.error("[" + type + "] - " + name + " - error", { error: error });
};

var useLazyQuery = function (query, options) {
    var _a = __read(useLazyQuery$1(query, options), 2), excecutable = _a[0], result = _a[1];
    var error = result.error;
    useEffect(function () {
        if (error) {
            handleError(query, error);
        }
    }, [error, query]);
    return [excecutable, result];
};

var useMutation = function (mutation, options) {
    var _a = __read(useMutation$1(mutation, options), 2), excecutable = _a[0], result = _a[1];
    var error = result.error;
    useEffect(function () {
        if (error) {
            handleError(mutation, error);
        }
    }, [error, mutation]);
    return [excecutable, result];
};

var useQuery = function (query, options) {
    var result = useQuery$1(query, options);
    var error = result.error;
    useEffect(function () {
        if (error) {
            handleError(query, error);
        }
    }, [error, query]);
    return result;
};

export { initApolloClient, useLazyQuery, useMutation, useQuery, wait };
//# sourceMappingURL=index.es.js.map
